import React from "react";

// One discount off the total - a percentage OR an amount, never both
// (Batch147/163). The discount reduces the taxable value, so VAT is charged on
// the net. Mirrors the server arithmetic exactly (sp_app_sales_*_set_discount,
// QuotationService.ComputeTotals) so a preview never disagrees with what saves.

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Resolved cash discount for a sub total, from the typed % or amount. */
export const resolveDiscount = (subTotal, percent, amount) => {
  const pct = Number(percent) || 0;
  if (pct > 0) return round2((subTotal * pct) / 100);
  return round2(Number(amount) || 0);
};

/** Sales pricing: one VAT rate over the whole sub total. */
export const computeDiscountedTotals = (subTotal, vatRate, percent, amount) => {
  const discount = resolveDiscount(subTotal, percent, amount);
  const net = subTotal - discount;
  const vat = round2((net * (Number(vatRate) || 0)) / 100);
  return { subTotal, discount, net, vat, total: net + vat, exceeds: discount > subTotal };
};

/** Quotations: lines keep their own VAT%. Lines are never altered - the VAT they
 *  carry is reduced by the same proportion as the discount. */
export const computeQuotationTotals = (lines, percent, amount) => {
  let subTotal = 0;
  let linesVat = 0;
  (lines || []).forEach((l) => {
    const lineAmount = (Number(l.qty) || 0) * (Number(l.rate) || 0);
    subTotal += lineAmount;
    linesVat += (lineAmount * (Number(l.vatPercent) || 0)) / 100;
  });
  const discount = resolveDiscount(subTotal, percent, amount);
  const net = subTotal - discount;
  const vat = subTotal > 0 ? round2((linesVat * net) / subTotal) : 0;
  return { subTotal, discount, net, vat, total: net + vat, exceeds: discount > subTotal };
};

/** Request fields for a discount typed as strings. Blank both -> nulls. */
export const discountRequest = (percent, amount) => ({
  discountPercent: String(percent ?? "").trim() ? Number(percent) : null,
  discountAmount: String(amount ?? "").trim() ? Number(amount) : null,
});

const money = (n) => Number(n || 0).toFixed(2);

/** Discount % / amount inputs. Typing in one clears the other. */
const DiscountFields = ({ percent, amount, onPercentChange, onAmountChange, colClass = "col-md-3", disabled = false }) => (
  <>
    <div className={`${colClass} mb-3`}>
      <label className="form-label">Discount %</label>
      <input
        type="number" min="0" max="100" step="0.01"
        className="form-control"
        placeholder="e.g. 10"
        value={percent}
        disabled={disabled}
        onChange={(e) => { onPercentChange(e.target.value); if (e.target.value) onAmountChange(""); }}
      />
    </div>
    <div className={`${colClass} mb-3`}>
      <label className="form-label">or Discount Amount</label>
      <input
        type="number" min="0" step="0.01"
        className="form-control"
        placeholder="e.g. 500"
        value={amount}
        disabled={disabled}
        onChange={(e) => { onAmountChange(e.target.value); if (e.target.value) onPercentChange(""); }}
      />
    </div>
  </>
);

/** Sub Total / Discount / Net / VAT / Total strip for a preview. */
export const DiscountedTotalsStrip = ({ totals, vatLabel = "VAT", percent }) => (
  <div className="d-flex flex-wrap gap-4 small">
    <span>Sub Total: <strong>{money(totals.subTotal)}</strong></span>
    {totals.discount > 0 && (
      <>
        <span className="text-danger">
          Discount{Number(percent) > 0 ? ` (${Number(percent)}%)` : ""}: <strong>-{money(totals.discount)}</strong>
        </span>
        <span>Net: <strong>{money(totals.net)}</strong></span>
      </>
    )}
    <span>{vatLabel}: <strong>{money(totals.vat)}</strong></span>
    <span className="text-primary">Total: <strong>{money(totals.total)}</strong></span>
    {totals.exceeds && <span className="text-danger">The discount is more than the sub total.</span>}
  </div>
);

// Thousands-grouped for the side panel, where the figures are read at a glance.
const grouped = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The same figures stacked for a narrow side panel, ending in a large, bold
 *  grand total - the number the user is actually agreeing to. */
export const TotalsSummary = ({ totals, vatLabel = "VAT", percent }) => {
  const row = (label, value, style) => (
    <div className="d-flex justify-content-between py-1" style={{ borderBottom: "1px dashed #E4E7EC", ...style }}>
      <span>{label}</span>
      <strong style={{ fontVariantNumeric: "tabular-nums" }}>{value}</strong>
    </div>
  );
  return (
    <div>
      {row("Sub Total", grouped(totals.subTotal))}
      {totals.discount > 0 && (
        <>
          {row(`Discount${Number(percent) > 0 ? ` (${Number(percent)}%)` : ""}`, `-${grouped(totals.discount)}`, { color: "#C62828" })}
          {row("Net", grouped(totals.net))}
        </>
      )}
      {row(vatLabel, grouped(totals.vat))}
      <div
        className="mt-3 px-3 py-2 rounded text-end"
        style={{ background: "#EEF7F1", border: "1.5px solid #1E8449" }}
      >
        <div className="text-uppercase fw-semibold" style={{ fontSize: 12, color: "#1E8449", letterSpacing: 0.5 }}>
          Total (incl. VAT)
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#14532D", lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
          {grouped(totals.total)}
        </div>
      </div>
      {totals.exceeds && <div className="text-danger small mt-2">The discount is more than the sub total.</div>}
    </div>
  );
};

export default DiscountFields;
